<template>
  <div class="notification-item-alone">
    <div class="notification-wrapper">
      <button
        class="notification-trigger"
        :aria-label="`Notificações${
          unreadCount > 0 ? `, ${unreadCount} não lidas` : ''
        }`"
        :class="{ 'has-unread': unreadCount > 0 }"
        @click.stop="toggleNotifications"
      >
        <i
          class="fas fa-bell"
          aria-hidden="true"
        />
        <span
          v-if="unreadCount > 0"
          class="notification-badge"
        >
          {{ unreadCount > 99 ? "99+" : unreadCount }}
        </span>
      </button>

      <div
        v-show="showNotifications"
        class="notification-dropdown"
        role="dialog"
        aria-label="Painel de notificações"
        @click.stop
      >
        <header class="notification-header">
          <div class="header-content">
            <h4 class="header-title">
              Notificações
            </h4>
            <span
              v-if="unreadCount > 0"
              class="unread-count"
            >
              {{ unreadCount }} nova{{ unreadCount !== 1 ? "s" : "" }}
            </span>
          </div>

          <div class="header-actions">
            <button
              v-if="notifications && notifications.length > 0"
              class="action-btn mark-all-read"
              title="Marcar todas como lidas"
              @click.stop="markAllAsRead"
            >
              <i
                class="fas fa-check-double"
                aria-hidden="true"
              />
              Marcar todas como lidas
            </button>
            <button
              v-if="notifications && notifications.length > 0"
              class="action-btn clear-all"
              title="Limpar todas"
              @click.stop="clearAllNotifications"
            >
              <i
                class="fas fa-trash"
                aria-hidden="true"
              />
              Limpar
            </button>
          </div>
        </header>

        <div class="notification-list-container">
          <div
            v-if="
              !loadingNotifications && notifications && notifications.length > 0
            "
            class="notification-list"
          >
            <div
              v-for="n in notifications"
              :key="n.Id"
              class="notification-item"
              :class="[getStatusClass(n.DueDate), { 'is-unread': !n.IsRead }]"
              role="button"
              tabindex="0"
              @click="handleNotificationClick(n)"
            >
              <div class="notification-status">
                <span
                  class="status-indicator"
                  :class="getStatusClass(n.DueDate)"
                />
              </div>

              <div class="notification-content">
                <div class="notification-header-row">
                  <h5 class="notification-title">
                    {{ n.Title }}
                  </h5>
                </div>

                <p class="notification-message">
                  {{ n.Message }}
                </p>

                <div class="notification-meta">
                  <div class="meta-row">
                    <span class="meta-item">
                      <span class="meta-label">Paciente:</span>
                      <span class="meta-value">{{ n.PatientName }}</span>
                    </span>
                  </div>

                  <div class="meta-row meta-footer">
                    <span class="meta-item">
                      <span class="meta-label">Prazo:</span>
                      <span class="meta-value">
                        {{ formatDate(n.DueDate) }}
                      </span>
                    </span>
                    <span class="time-ago">{{ n.TimeAgo }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            v-else-if="loadingNotifications"
            class="loading-state"
          >
            <div class="spinner" />
            <p>Carregando notificações...</p>
          </div>

          <div
            v-else
            class="empty-state"
          >
            <p class="empty-title">
              Nenhuma notificação
            </p>
            <p class="empty-description">
              Quando houverem demandas que precisam da sua atenção, elas
              aparecerão aqui
            </p>
          </div>
        </div>

        <footer
          v-if="notifications && notifications.length > 0"
          class="notification-footer-bar"
        >
          <button
            class="view-all-btn"
            @click="viewAllNotifications"
          >
            Ver todas as notificações
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "NotificationDropdown",

  data() {
    return {
      showNotifications: false,
      notifications: [],
      loadingNotifications: false
    };
  },

  computed: {
    unreadCount() {
      if (!this.notifications) return 0;
      return this.notifications.filter((n) => !n.IsRead).length;
    }
  },

  created() {
    this.getNotifications();
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
    document.addEventListener("keydown", this.handleEscape);
  },

  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
    document.removeEventListener("keydown", this.handleEscape);
  },
  methods: {
    handleClickOutside(event) {
      const notificationWrapper = this.$el;
      if (
        this.showNotifications &&
        !notificationWrapper.contains(event.target)
      ) {
        this.showNotifications = false;
      }
    },

    handleEscape(event) {
      if (event.key === "Escape" && this.showNotifications) {
        this.showNotifications = false;
      }
    },

    toggleNotifications() {
      this.showNotifications = !this.showNotifications;
    },

    async getNotifications() {
      this.loadingNotifications = true;

      return this.$axios
        .get("/api/notifications", {
          headers: {
            Authorization: `bearer ${
              this.$Session.get(this.$userSessionName).token.Value
            }`
          }
        })
        .then((res) => {
          this.notifications = res.data;
        })
        .catch((error) => {
          this.$Utilities.verifyToken(error.response.status);
          console.error("Erro ao carregar notificações:", error);
        })
        .finally(() => {
          this.loadingNotifications = false;
        });
    },

    getStatusClass(dueDate) {
      const now = new Date();
      const due = new Date(dueDate);
      const diffDays = Math.ceil(
        (due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
      );

      if (diffDays < 0) return "status-overdue";

      if (diffDays <= 1) return "status-critical";

      if (diffDays <= 3) return "status-warning";

      if (diffDays <= 7) return "status-attention";

      return "status-ok";
    },

    formatDate(date) {
      return this.$Utilities.formatDate(
        date,
        "YYYY-MM-DDTHH:mm:ss",
        "DD/MM/YYYY"
      );
    },

    async handleNotificationClick(notification) {
      if (!notification.IsRead) {
        await this.markAsRead(notification.Id);
      }

      this.showNotifications = false;

      await this.$router.push({
        name: "myPatientActions",
        params: {
          id: notification.PatientId
        }
      });
      window.location.reload();
    },

    async markAsRead(id) {
      const notificationIndex = this.notifications.findIndex(
        (notification) => notification.Id === id
      );

      if (notificationIndex === -1) return;

      this.notifications[notificationIndex].IsRead = true;

      const body = [id];

      try {
        await this.$axios.put("/api/notifications/mark-as-read", body, {
          headers: {
            Authorization: `bearer ${
              this.$Session.get(this.$userSessionName).token.Value
            }`
          }
        });
      } catch (error) {
        this.notifications[notificationIndex].IsRead = false;
      }
    },

    markAllAsRead() {
      this.notifications.forEach((n) => (n.IsRead = true));

      this.$axios
        .put(
          "/api/notifications/mark-all-as-read",
          {},
          {
            headers: {
              Authorization: `bearer ${
                this.$Session.get(this.$userSessionName).token.Value
              }`
            }
          }
        )
        .catch((error) => {
          console.error("Erro ao marcar todas como lidas:", error);
        });
    },

    async clearAllNotifications() {
      try {
        await this.$axios.delete("/api/notifications/delete-all", {
          headers: {
            Authorization: `bearer ${
              this.$Session.get(this.$userSessionName).token.Value
            }`
          }
        });

        this.notifications = [];
        await this.getNotifications();
      } catch (error) {
        this.$Utilities.verifyToken(error.response?.status);
      }
    },
    viewAllNotifications() {
      this.showNotifications = false;
      this.$router.push({ name: "notifications" });
    }
  }
};
</script>

<style scoped>
.notification-item-alone {
  position: relative;
}

.notification-wrapper {
  position: relative;
}

/* Botão de ícone compacto na faixa superior */
.notification-trigger {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;

  border: none;
  border-radius: 50%;
  background-color: #e9e9e9;
  cursor: pointer;

  color: #797979;
  font-size: 20px;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.notification-trigger:hover {
  background-color: #dcdcdc;
  color: #9a397d;
}

.notification-trigger.has-unread {
  color: #9a397d;
}

.notification-trigger:focus-visible {
  outline: 2px solid #007dc3;
  outline-offset: 2px;
}

.notification-badge {
  position: absolute;
  top: 2px;
  right: 2px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;

  display: flex;
  align-items: center;
  justify-content: center;

  background: #9a397d;
  color: #ffffff;
  border: 1px solid #ffffff;
  border-radius: 8px;
  font-size: 9px;
  font-weight: 600;
}

/* Painel abre abaixo, alinhado à direita do sino */
.notification-dropdown {
  position: absolute;
  top: calc(100% + 12px);
  right: 0;
  width: 420px;
  max-height: 600px;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 5px;
  box-shadow: 0px 3px 6px #00000029;
  overflow: hidden;
  z-index: 1000;
}
.notification-header {
  padding: 16px 20px;
  border-bottom: 1px solid #dfdfdf;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
}

.header-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #4f3f82;
}

.unread-count {
  padding: 4px 10px;
  background: rgba(154, 57, 125, 0.1);
  color: #9a397d;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 4px 8px;
  background: transparent;
  border: 1px solid #dfdfdf;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  color: #797979;
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-btn:hover {
  background: #f6f6f6;
  border-color: #9a397d;
  color: #4f3f82;
}

.action-btn:focus-visible {
  outline: 2px solid #9a397d;
  outline-offset: 2px;
}

.action-btn i {
  font-size: 12px;
}

.action-btn.clear-all:hover {
  background: rgba(219, 69, 70, 0.05);
  border-color: #db4545;
  color: #db4545;
}

.notification-list-container {
  flex: 1;
  overflow-y: auto;
  max-height: 400px;
}

.notification-list {
  padding: 8px;
}

.notification-item {
  display: flex;
  gap: 10px;
  padding: 12px;
  margin-bottom: 6px;
  background: #ffffff;
  border: 1px solid #dfdfdf;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.notification-item:hover {
  background: #f9f9f9;
  border-color: #c0c0c0;
}

.notification-item:focus-visible {
  outline: 2px solid #9a397d;
  outline-offset: 2px;
}

.notification-item.is-unread {
  background: rgba(154, 57, 125, 0.03);
  border: 1px solid #9a397d;
}

.notification-item.is-unread:hover {
  background: rgba(154, 57, 125, 0.06);
}

.notification-status {
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  padding-top: 2px;
}

.status-indicator {
  display: block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.8);
}

.status-indicator.status-overdue {
  background: #db4545;
}

.status-indicator.status-critical {
  background: #ff6b6b;
}

.status-indicator.status-warning {
  background: #dfc12a;
}

.status-indicator.status-attention {
  background: #f39c12;
}

.status-indicator.status-ok {
  background: #35a29d;
}

.notification-content {
  flex: 1;
  min-width: 0;
}

.notification-header-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}

.notification-title {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: #4f3f82;
  line-height: 1.4;
  word-break: break-word;
}

.notification-message {
  margin: 0 0 8px 0;
  font-size: 12px;
  font-weight: 400 !important;
  line-height: 1.5;
  color: #484848;
  word-break: break-word;
}

.notification-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.meta-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
}

.meta-label {
  color: #797979;
  font-weight: 500;
}

.meta-value {
  color: #4f3f82;
  font-weight: 500;
}

.meta-footer {
  justify-content: space-between;
  padding-top: 6px;
  margin-top: 3px;
  border-top: 1px solid #dfdfdf;
}

.time-ago {
  font-size: 10px;
  font-weight: 500;
  color: #a8a8a8;
  white-space: nowrap;
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 40px;
  text-align: center;
}

.spinner {
  width: 40px;
  height: 40px;
  margin-bottom: 16px;
  border: 3px solid #dfdfdf;
  border-top-color: #9a397d;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-state p {
  margin: 0;
  font-size: 14px;
  color: #797979;
}

.empty-title {
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 600;
  color: #4f3f82;
  pointer-events: none;
}

.empty-description {
  margin: 0;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.5;
  color: #797979;
  pointer-events: none;
}

.notification-footer-bar {
  padding: 12px 20px;
  background: #f6f6f6;
  border-top: 1px solid #dfdfdf;
}

.view-all-btn {
  width: 100%;
  padding: 10px;
  background: #9a397d;
  border: 1px solid #9a397d;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
}

.view-all-btn:hover {
  background: #7d2e65;
  border-color: #7d2e65;
}

.view-all-btn:focus-visible {
  outline: 2px solid #9a397d;
  outline-offset: 2px;
}

.notification-list-container::-webkit-scrollbar {
  width: 8px;
}

.notification-list-container::-webkit-scrollbar-track {
  margin: 20px 0;
  background: #f0f0f0;
  border-radius: 50px;
}

.notification-list-container::-webkit-scrollbar-thumb {
  background: #9a397d;
  border-radius: 50px;
  transition: background 0.2s ease;
}

.notification-list-container::-webkit-scrollbar-thumb:hover {
  background: #7d2e65;
}

/* Em telas pequenas o painel ocupa a largura da viewport, abaixo da faixa */
@media screen and (max-width: 768px) {
  .notification-dropdown {
    position: fixed;
    top: 56px;
    left: 8px;
    right: 8px;
    width: auto;
  }
}
</style>
